import { applePay, ApplePay, client as braintreeClient } from 'braintree-web';
import { ApplePayPaymentRequest } from 'braintree-web/modules/apple-pay';
import { Dispatch } from 'redux';
import { change, submit } from 'redux-form';
import { getAndSetBraintreeToken as getAndSetBraintreeTokenAction } from '../actions/order/common';
import { CHECKOUT_FORM_NAME } from '../constants';
import { CheckoutState } from '../state';
import { AppNamespace } from '../state/app';
import { EventNamespace } from '../state/event';
import { OrderNamespace } from '../state/order';

declare global {
    interface Window {
        ApplePaySession: typeof ApplePaySession;
    }
}

export enum ApplePayActionType {
    setApplepayPaymentRequest = 'setApplepayPaymentRequest',
    setApplepayInstance = 'setApplepayInstance',
    setCanMakePaymentsWithActiveCard = 'setCanMakePaymentsWithActiveCard',
}

export interface SetApplePayPaymentRequestAction {
    type: ApplePayActionType.setApplepayPaymentRequest;
    payload: {
        applepay: {
            applepayPaymentRequest: ApplePayPaymentRequest;
        };
    };
}

export interface SetApplePayInstanceAction {
    type: ApplePayActionType.setApplepayInstance;
    payload: {
        applepay: {
            applepayInstance: ApplePay;
        };
    };
}

export interface SetCanMakePaymentsWithActiveCardAction {
    type: ApplePayActionType.setCanMakePaymentsWithActiveCard;
    payload: {
        applepay: {
            canMakePaymentsWithActiveCard: boolean;
        };
    };
}

export const setApplepayPaymentRequest = (
    applepayPaymentRequest: ApplePayPaymentRequest,
): SetApplePayPaymentRequestAction => ({
    type: ApplePayActionType.setApplepayPaymentRequest,
    payload: {
        applepay: {
            applepayPaymentRequest,
        },
    },
});
export const setApplepayInstance = (
    applepayInstance: ApplePay,
): SetApplePayInstanceAction => ({
    type: ApplePayActionType.setApplepayInstance,
    payload: {
        applepay: {
            applepayInstance,
        },
    },
});
export const setCanMakePaymentsWithActiveCard = (
    canMakePaymentsWithActiveCard: boolean,
): SetCanMakePaymentsWithActiveCardAction => ({
    type: ApplePayActionType.setCanMakePaymentsWithActiveCard,
    payload: {
        applepay: {
            canMakePaymentsWithActiveCard,
        },
    },
});

const getApplePayPaymentRequest = async (
    state: CheckoutState,
    applepayInstance: ApplePay,
): Promise<ApplePayPaymentRequest> => {
    const { order, event, app } = state as {
        order: OrderNamespace;
        event: EventNamespace;
        app: AppNamespace;
    };

    const payRequest = {
        total: {
            label: `Tickets for ${event.name.text}`,
            amount: `${order.costs.displayPrice.majorValue}`,
        },
        currencyCode: `${event.currency}`,
        merchantCapabilities: ['supports3DS', 'supportsDebit'],
        supportedNetworks: ['masterCard', 'visa'],
        countryCode: `${app.defaultUserCountry}`,
    };

    return applepayInstance.createPaymentRequest(payRequest);
};

async function getApplePayInstance(order: OrderNamespace) {
    const braintreeToken = order.braintreeClientToken;
    const client = await braintreeClient.create({
        authorization: braintreeToken,
    });
    return applePay.create({
        client,
    });
}

export const onApplePayMethodSelected = () => async (
    dispatch: Dispatch<any>,
    getState: () => CheckoutState,
) => {
    try {
        dispatch(getAndSetBraintreeTokenAction());
        const state = getState();
        const applepayInstance: ApplePay = await getApplePayInstance(
            state.order as OrderNamespace,
        );

        dispatch({
            type: ApplePayActionType.setApplepayInstance,
            payload: {
                applepayInstance,
            },
        });
        dispatch({
            type: ApplePayActionType.setApplepayPaymentRequest,
            payload: {
                applepayPaymentRequest: await getApplePayPaymentRequest(
                    state,
                    applepayInstance,
                ),
            },
        });
    } catch (error) {
        console.log(error);
    }
};

export const startApplePaySession = () => (
    dispatch: Dispatch<CheckoutState>,
    getState: () => CheckoutState,
) => {
    const { applepay } = getState();

    if (!window.ApplePaySession || !applepay.applepayInstance) {
        return;
    }

    try {
        const session = new window.ApplePaySession(
            3,
            applepay.applepayPaymentRequest as ApplePayJS.ApplePayPaymentRequest,
        );

        session.onvalidatemerchant = (event) => {
            (applepay.applepayInstance as ApplePay).performValidation(
                {
                    validationURL: event.validationURL,
                    displayName: 'eventbrite',
                },
                function (err, validationData) {
                    if (err) {
                        console.error(err);
                        session.abort();
                        return;
                    }
                    session.completeMerchantValidation(validationData);
                },
            );
        };

        session.onpaymentauthorized = (event) => {
            (applepay.applepayInstance as ApplePay).tokenize(
                {
                    token: event.payment.token,
                },
                function (tokenizeErr, payload) {
                    if (tokenizeErr) {
                        console.error(
                            'Error tokenizing Apple Pay:',
                            tokenizeErr,
                        );
                        session.completePayment(ApplePaySession.STATUS_FAILURE);
                        return;
                    }

                    dispatch(
                        change(
                            CHECKOUT_FORM_NAME,
                            'payment.applePayBraintreeNonce',
                            payload.nonce,
                        ),
                    );

                    // After you have transacted with the payload.nonce,
                    // call `completePayment` to dismiss the Apple Pay sheet.
                    session.completePayment(ApplePaySession.STATUS_SUCCESS);

                    dispatch(submit(CHECKOUT_FORM_NAME));
                },
            );
        };

        session.oncancel = () => {
            session.completePayment(ApplePaySession.STATUS_FAILURE);
        };

        session.begin();
    } catch (error) {
        console.log(error);
    }
};

export type ApplePayAction =
    | SetApplePayPaymentRequestAction
    | SetApplePayInstanceAction
    | SetCanMakePaymentsWithActiveCardAction;
